
import GaugeComponent from "react-gauge-component";

interface TemperatureGaugeConfig {
  title?: string;
  value?: number | string | null;
  unit?: "C" | "F";
  min_temp_c?: number;
  max_temp_c?: number;
  timestamp?: string | null;
  device_label?: {
    label?: string;
    unit?: string;
  } | null;
}

interface TemperatureGaugeComponentProps {
  config: TemperatureGaugeConfig;
}

const celsiusToFahrenheit = (value: number) => (value * 9) / 5 + 32;

export function TemperatureGaugeComponent({
  config,
}: TemperatureGaugeComponentProps) {
  const displayUnit = config.unit === "F" ? "F" : "C";

  const rawValue =
    config.value === null ||
    config.value === undefined ||
    config.value === ""
      ? null
      : Number(config.value);

  const hasData = rawValue !== null && Number.isFinite(rawValue);

  const minC = config.min_temp_c ?? 0;
  const maxC = config.max_temp_c ?? 100;

  const convertTemperature = (value: number) =>
    displayUnit === "F" ? celsiusToFahrenheit(value) : value;

  const minValue = convertTemperature(minC);
  const maxValue = convertTemperature(maxC);
  const value = hasData ? convertTemperature(rawValue!) : null;

  const gaugeValue =
    value === null
      ? minValue
      : Math.min(maxValue, Math.max(minValue, value));

  const range = maxValue - minValue;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Gauge area - uses remaining height */}
      <div
        style={{
          flex: "1 1 0",
          minWidth: 0,
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {hasData ? (
          <GaugeComponent
            type="semicircle"
            style={{
              width: "100%",
              height: "100%",
            }}
            minValue={minValue}
            maxValue={maxValue}
            value={gaugeValue}
            arc={{
              width: 0.24,
              padding: 0,
              cornerRadius: 0,
              subArcs: [
                {
                  limit: minValue + range * 0.20,
                  color: "#FFD600",
                },
                {
                  limit: minValue + range * 0.40,
                  color: "#FFAB00",
                },
                {
                  limit: minValue + range * 0.60,
                  color: "#FF8000",
                },
                {
                  limit: minValue + range * 0.80,
                  color: "#F4511E",
                },
                {
                  limit: maxValue,
                  color: "#E62117",
                },
              ],
            }}
            pointer={{
              type: "needle",
              color: "#555555",
              length: 0.78,
              width: 12,
            }}
            labels={{
              valueLabel: {
                formatTextValue: () =>
                  `${value!.toFixed(1)} °${displayUnit}`,
                style: {
                  fill: "#ffffff",
                  fontSize: "24px",
                  textShadow: "none",
                },
              },
              tickLabels: {
                type: "outer",
                ticks: [
                  { value: minValue },
                  { value: minValue + range * 0.5 },
                  { value: maxValue },
                ],
                defaultTickValueConfig: {
                  formatTextValue: (tickValue: number) =>
                    tickValue.toFixed(0),
                  style: {
                    fill: "#cccccc",
                    fontSize: "10px",
                  },
                },
              },
            }}
          />
        ) : (
          <div
            className="d-flex flex-column align-items-center justify-content-center text-muted"
            style={{ width: "100%", height: "100%" }}
          >
            <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>
              NO DATA
            </div>
            <small>Waiting for temperature reading</small>
          </div>
        )}
      </div>

      {/* Fixed timestamp footer */}
      <div
        style={{
          flex: "0 0 22px",
          height: "22px",
          minHeight: "22px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          color: "#ffffff",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          padding: "0 4px",
        }}
        title={config.timestamp || ""}
      >
        {config.timestamp || "--"}
      </div>
    </div>
  );
}

export default TemperatureGaugeComponent;
